import React, { createContext, useState, useContext, useEffect, useRef, useCallback, useMemo } from 'react';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import { serviceUrls } from '../apis/axiosInstance';
import { useAuth } from './AuthContext';
import {
    markAsRead as markAsReadApi, getNotifications,
    markAsReadMyBusiness, getMyBusinessNotifications,
} from '../apis/notificationApi';

const NotificationContext = createContext();

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const { userId, reloadUserData } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadNotifications, setUnreadNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    // One entry per backend that runs its own embedded STOMP broker (see connectWebSocket) -
    // fpo and mybusiness each mirror the identical WebSocketConfig/`/ws` setup, but a Spring
    // simple broker is in-process per application, not shared between them. A single client only
    // ever heard whichever one it connected to.
    const stompClientRefs = useRef([]);

    const removeNotification = useCallback((id) => {
        setNotifications(prev => prev.filter(notification => notification.id !== id));
    }, []);

    const addNotification = useCallback((notification) => {
        const id = Date.now() + Math.random();
        const newNotification = {
            id,
            ...notification,
        };

        setNotifications(prev => [...prev, newNotification]);

        if (notification.autoClose !== false) {
            setTimeout(() => {
                removeNotification(id);
            }, notification.duration || 5000);
        }

        return id;
    }, [removeNotification]);

    const showInfo = useCallback((message, options = {}) => {
        return addNotification({ type: 'info', message, ...options });
    }, [addNotification]);

    const showSuccess = useCallback((message, options = {}) => {
        return addNotification({ type: 'success', message, ...options });
    }, [addNotification]);

    const showError = useCallback((message, options = {}) => {
        return addNotification({ type: 'error', message, ...options });
    }, [addNotification]);

    const showWarning = useCallback((message, options = {}) => {
        return addNotification({ type: 'warning', message, ...options });
    }, [addNotification]);

    const clearAllNotifications = useCallback(() => {
        setNotifications([]);
    }, []);

    // Queries fpo AND mybusiness (2026-09-17) - two independent backends, each with their own
    // notifications table for their own users (mybusiness's own NotificationController mirrors
    // fpo's shape exactly but is a genuinely separate API on a separate port). Fetching only
    // fpo's meant an admin's unreadCount (and everything gated on it, like
    // AdminUsersDataTable's live-refresh useEffect) never reflected a mybusiness-originated
    // notification at all, no matter how promptly the live STOMP push itself now arrives -
    // confirmed live: a real "New Company Registration" notification saved correctly to
    // mybusiness's own table and pushed over its own (now-connected) broker, yet unreadCount
    // never ticked because the very fetch meant to confirm it only ever asked fpo. Each row is
    // tagged with `_source` so markNotificationAsRead() below knows which backend's mark-as-read
    // endpoint to call. A failure on one side (e.g. that backend temporarily down) still shows
    // the other's notifications rather than losing both.
    const fetchNotifications = useCallback(async () => {
        const [fpoResult, myBusinessResult] = await Promise.allSettled([
            getNotifications(),
            getMyBusinessNotifications(),
        ]);

        const mapRows = (result, source) => {
            if (result.status !== 'fulfilled') {
                console.error(`Failed to fetch ${source} notifications`, result.reason);
                return [];
            }
            return (result.value.data.data || []).map(n => ({
                id: n.id,
                message: n.message,
                read: n.readStatus,
                timestamp: n.createDate || new Date(),
                title: n.title,
                type: n.type,
                _source: source,
            }));
        };

        const fetched = [
            ...mapRows(fpoResult, 'fpo'),
            ...mapRows(myBusinessResult, 'myBusiness'),
        ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        setUnreadNotifications(fetched);
        setUnreadCount(fetched.filter(n => !n.read).length);
    }, []);

    const handleNewNotification = useCallback((message) => {
        showInfo(message, { autoClose: true });
        fetchNotifications();
    }, [fetchNotifications, showInfo]);

    // User-specific notifications (approve/reject/expiry) mean this user's own access may have
    // just changed — reload roles/permissions/accessibilityRules/subscriptions so the app
    // reflects it live, without requiring logout/login. Admin-broadcast notifications are about
    // *other* users' submissions and don't affect the admin's own access, so they skip this.
    const handleUserNotification = useCallback((message) => {
        handleNewNotification(message);
        reloadUserData();
    }, [handleNewNotification, reloadUserData]);

    // Uses a webSocketFactory (a function that hands back a fresh SockJS instance on every
    // call) rather than a single pre-built socket, so the client can actually recreate the
    // transport and reconnect on its own after a drop. The old Stomp.over(socket) pattern this
    // replaced took an already-created socket, which the library only warns about ("auto
    // reconnect will not work") rather than erroring on - so the first connection looked fine
    // in testing but silently never came back after any network blip, with only a
    // console.error to show for it.
    //
    // Connects one STOMP client per backend (2026-09-17) - fpo and mybusiness each run their own
    // embedded broker (WebSocketConfig.java in both, deliberately mirrored) on their own port;
    // Spring's simple broker is in-process, never shared across two separate Spring Boot apps.
    // A single client connected only to fpo's `/ws` meant any notification actually triggered
    // from WITHIN mybusiness's backend (e.g. CompanyServiceImpl's "New Company Registration",
    // or the PENDING subscription_request raised by addCompanyToUser()) published to a broker
    // this frontend was never listening to - the notification row still saved correctly (so a
    // manual reload always "fixed" it), but no live push ever reached an open admin session.
    // Confirmed live: an admin sitting on Users -> Subscription Requests never saw a new
    // MyBusiness-side request appear without reloading, while an equivalent fpo-side one (e.g.
    // /subscribe) updated instantly - same handlers, same topics, only the broker differed.
    const connectToBroker = useCallback((socketUrl) => {
        const client = new Client({
            webSocketFactory: () => new SockJS(socketUrl),
            connectHeaders: { Authorization: `Bearer ${sessionStorage.getItem("accessToken")}` },
            reconnectDelay: 5000,
            onConnect: () => {
                console.log('Connected to WebSocket:', socketUrl);
                // The broker is fire-once with no redelivery -- any push sent while this
                // client was disconnected/reconnecting is gone by the time it reconnects.
                // Re-syncing here (on the initial connect and every reconnect) backfills
                // whatever was missed, instead of only picking it up on the next full
                // page load.
                fetchNotifications();
                // A user-specific event (e.g. "Subscription Approved") that arrived while this
                // socket was disconnected/reconnecting only gets backfilled into the notification
                // LIST above - it never goes through handleUserNotification (that only runs for
                // a LIVE STOMP push), so reloadUserData() was never called for it. The user's own
                // access (fullUserData/accessibilityRules/userSubscriptions) stayed stale even
                // though the bell correctly showed the approval once reconnected - confirmed
                // live: sitting on SubscriptionPage.jsx with the socket dropped at approval time,
                // the page never left "Request Pending Approval" despite the notification
                // eventually appearing. Reloading here too, on every (re)connect, closes that gap
                // - redundant on a fresh page load (fullUserData is already current then), and
                // now also redundant across the two brokers connecting close together - harmless
                // either way, reloadUserData() just re-fetches the same current state twice.
                reloadUserData();
                // Every notification - including review/approval notices for admin-ish roles -
                // is now targeted server-side (see NotificationServiceImpl.
                // notifyUsersWithPermission()) and pushed over this same per-user topic, so
                // there's no separate admin-broadcast topic to subscribe to here.
                if (userId) {
                    client.subscribe(`/topic/notifications/${userId}`, (message) => {
                        if (message.body) {
                            handleUserNotification(message.body);
                        }
                    });
                }
            },
            onStompError: (frame) => {
                console.error('STOMP error:', socketUrl, frame.headers?.message, frame.body);
            },
            onWebSocketError: (event) => {
                console.error('WebSocket error:', socketUrl, event);
            },
        });

        client.activate();
        return client;
    }, [handleNewNotification, handleUserNotification, userId, fetchNotifications, reloadUserData]);

    const connectWebSocket = useCallback(() => {
        stompClientRefs.current = [
            connectToBroker(serviceUrls.myFPO + '/ws'),
            connectToBroker(serviceUrls.myBusiness + '/ws'),
        ];
    }, [connectToBroker]);

    const disconnectWebSocket = useCallback(() => {
        stompClientRefs.current.forEach((client) => client.deactivate());
        stompClientRefs.current = [];
    }, []);

    useEffect(() => {
        if (!userId) return;
        connectWebSocket();
        return () => {
            disconnectWebSocket();
        };
    }, [userId, connectWebSocket, disconnectWebSocket]);

    // Fetch past notifications when userId is available
    useEffect(() => {
        if (userId) {
            fetchNotifications();
        }
    }, [userId, fetchNotifications]);

    // Routes to whichever backend this notification actually came from (2026-09-17) - a
    // mybusiness-sourced id hitting fpo's mark-as-read endpoint (or vice versa) would 404/no-op
    // silently, since each backend only knows its own notifications table.
    const markAsReadOnSource = (id, source) =>
        source === 'myBusiness' ? markAsReadMyBusiness(id) : markAsReadApi(id);

    const markNotificationAsRead = useCallback(async (id, index) => {
        try {
            let source;
            setUnreadNotifications(prev => {
                const updated = [...prev];
                if (updated[index]) {
                    updated[index] = { ...updated[index], read: true };
                    source = updated[index]._source;
                }
                return updated;
            });
            setUnreadCount(prev => Math.max(0, prev - 1));

            if (typeof id === 'string' && id.length > 10) {
                await markAsReadOnSource(id, source);
            }
        } catch (error) {
            console.error("Failed to mark read", error);
        }
    }, []);

    // No bulk "mark all read" endpoint exists server-side, so each unread notification is marked
    // individually - mirrors markNotificationAsRead's per-item id/source routing, just fanned out.
    const markAllNotificationsAsRead = useCallback(async () => {
        const toMark = unreadNotifications.filter(n => !n.read).map(n => ({ id: n.id, source: n._source }));
        if (toMark.length === 0) return;

        setUnreadNotifications(prev => prev.map(n => ({ ...n, read: true })));
        setUnreadCount(0);

        await Promise.all(toMark.map(({ id, source }) => {
            if (typeof id === 'string' && id.length > 10) {
                return markAsReadOnSource(id, source).catch(error => console.error("Failed to mark read", error));
            }
            return Promise.resolve();
        }));
    }, [unreadNotifications]);

    const value = useMemo(() => ({
        notifications,
        addNotification,
        removeNotification,
        clearAllNotifications,
        showSuccess,
        showError,
        showInfo,
        showWarning,
        unreadNotifications,
        unreadCount,
        markNotificationAsRead,
        markAllNotificationsAsRead
    }), [
        notifications,
        addNotification,
        removeNotification,
        clearAllNotifications,
        showSuccess,
        showError,
        showInfo,
        showWarning,
        unreadNotifications,
        unreadCount,
        markNotificationAsRead,
        markAllNotificationsAsRead
    ]);

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};
